import React, { useState, useEffect } from 'react';
import type { DentalServiceCatalog } from '../../types';

interface ServiceFormProps {
  service?: DentalServiceCatalog;
  onSubmit: (data: Partial<DentalServiceCatalog>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const categories = [
  { value: 'diagnostic', label: 'Greining' },
  { value: 'preventive', label: 'Forvarnarmeðferð' },
  { value: 'restorative', label: 'Viðgerð' },
  { value: 'endodontics', label: 'Rótarmeðferð' },
  { value: 'periodontics', label: 'Tannholdslækning' },
  { value: 'oral_surgery', label: 'Munnholsaðgerð' },
];

export function ServiceForm({ service, onSubmit, onCancel, isLoading }: ServiceFormProps) {
  const [formData, setFormData] = useState({
    cdt_code: service?.cdt_code || '',
    name: service?.name || '',
    name_is: service?.name_is || '',
    category: service?.category || 'diagnostic',
    default_duration_minutes: service?.default_duration_minutes || 30,
    base_price: service?.base_price || 0,
    description: service?.description || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (service) {
      setFormData({
        cdt_code: service.cdt_code || '',
        name: service.name || '',
        name_is: service.name_is || '',
        category: service.category || 'diagnostic',
        default_duration_minutes: service.default_duration_minutes,
        base_price: service.base_price || 0,
        description: service.description || '',
      });
    }
  }, [service]);

  const validateCDTCode = (code: string): boolean => {
    const pattern = /^D\d{4}$/;
    return pattern.test(code);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    // Validation
    if (!formData.cdt_code.trim()) {
      newErrors.cdt_code = 'CDT kóði er áskilinn';
    } else if (!validateCDTCode(formData.cdt_code)) {
      newErrors.cdt_code = 'CDT kóði verður að vera á sniðinu D#### (t.d. D0120)';
    }

    if (!formData.name_is.trim()) {
      newErrors.name_is = 'Heiti á íslensku er áskilið';
    }

    if (formData.default_duration_minutes < 15 || formData.default_duration_minutes > 480) {
      newErrors.default_duration_minutes = 'Tími verður að vera á bilinu 15-480 mínútur';
    }

    if (formData.base_price < 0) {
      newErrors.base_price = 'Verð getur ekki verið neikvætt';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'default_duration_minutes' || name === 'base_price' ? Number(value) : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* CDT Code */}
      <div>
        <label htmlFor="cdt_code" className="block text-sm font-medium text-gray-700 mb-1">
          CDT Kóði *
        </label>
        <input
          type="text"
          id="cdt_code"
          name="cdt_code"
          value={formData.cdt_code}
          onChange={handleChange}
          placeholder="D0120"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono ${
            errors.cdt_code ? 'border-red-300' : 'border-gray-300'
          }`}
          disabled={!!service}
        />
        {errors.cdt_code && (
          <p className="mt-1 text-sm text-red-600">{errors.cdt_code}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">Snið: D#### (t.d. D0120, D1110)</p>
      </div>

      {/* Name Icelandic */}
      <div>
        <label htmlFor="name_is" className="block text-sm font-medium text-gray-700 mb-1">
          Heiti (íslenska) *
        </label>
        <input
          type="text"
          id="name_is"
          name="name_is"
          value={formData.name_is}
          onChange={handleChange}
          placeholder="Tannrækt"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name_is ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {errors.name_is && (
          <p className="mt-1 text-sm text-red-600">{errors.name_is}</p>
        )}
      </div>

      {/* Name English */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Heiti (enska)
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Prophylaxis - Adult"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Category */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Flokkur *
        </label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Duration and Price Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Duration */}
        <div>
          <label htmlFor="default_duration_minutes" className="block text-sm font-medium text-gray-700 mb-1">
            Tími (mínútur) *
          </label>
          <input
            type="number"
            id="default_duration_minutes"
            name="default_duration_minutes"
            value={formData.default_duration_minutes}
            onChange={handleChange}
            min="15"
            max="480"
            step="15"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.default_duration_minutes ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.default_duration_minutes && (
            <p className="mt-1 text-sm text-red-600">{errors.default_duration_minutes}</p>
          )}
        </div>

        {/* Price */}
        <div>
          <label htmlFor="base_price" className="block text-sm font-medium text-gray-700 mb-1">
            Verð (ISK) *
          </label>
          <input
            type="number"
            id="base_price"
            name="base_price"
            value={formData.base_price}
            onChange={handleChange}
            min="0"
            step="100"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.base_price ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.base_price && (
            <p className="mt-1 text-sm text-red-600">{errors.base_price}</p>
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Lýsing
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          placeholder="Nánar um meðferðina..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          disabled={isLoading}
        >
          Hætta við
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? 'Vinnur...' : service ? 'Uppfæra' : 'Stofna'}
        </button>
      </div>
    </form>
  );
}
